import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { color } from 'styles/colors'

import styled from 'styled-components'
import Plot from 'react-plotly.js'

import Card from 'components/Card'
import StatusButton from 'pages/Dashboard/cards/shared/StatusButton'

import useDeviceStatuses from 'hooks/useDeviceStatuses'
import useScreenSize from 'hooks/useScreenSize'

import { screenSize } from 'styles/shared'

type Props = {
  connectedCount: number
  disconnectedCount: number
  isLoaded: boolean
}

type IChartData = {
  labels: string[],
  values: number[],
  colors: string[],
  shape: string[]
}

const CONNECTED = 'Connected'
const DISCONNECTED = 'Disconnected'

const mapModelNames = {
  'XR724': '724',
  'XL824': '824',
  'XL850': '850',
  'XL1050': '1050',
  'SC360': 'SC360',
  'PIVOT': 'Pivot'
}

const ConnectionStatusCard = ({ connectedCount, disconnectedCount, isLoaded = true }: Props) => {
  const { isDeviceLoaded, deviceModels } = useDeviceStatuses()
  const screenSize = useScreenSize()
  const navigate = useNavigate()

  const imageWidth = screenSize === 'small' ? 70 : 90

  const chartWidth = useMemo(() => {
    if (screenSize === 'medium') {
      return 120
    } else if (screenSize === 'small') {
      return 78
    } else {
      return 90
    }
  }, [screenSize])

  const plotDatas: Array<Partial<Plotly.PieData>> = deviceModels.map((device) => {
    const chartData: IChartData = {
      labels: [],
      values: [],
      colors: [],
      shape: []
    }
    if (device.connected > 0) {
      chartData.labels.push(CONNECTED)
      chartData.values.push(device.connected)
      chartData.colors.push(color(CONNECTED.toLowerCase()))
      chartData.shape.push('')
    }
    if (device.disconnected > 0) {
      chartData.labels.push(DISCONNECTED)
      chartData.values.push(device.disconnected)
      chartData.colors.push(color(DISCONNECTED.toLowerCase()))
      chartData.shape.push('\\')
    }
    return {
      title: { text: mapModelNames[device.model] ?? device.model },
      titleposition: 'top center',
      values: chartData.values,
      titlefont: {
        family: 'Inter',
        size: 13,
        weight: 600,
        textcase: 'upper'
      },
      labels: chartData.labels,
      marker: {
        colors: chartData.colors,
        pattern: {
          shape: chartData.shape,
          fillmode: 'overlay',
          fgopacity: 1,
          solidity: '0.1'
        }
      },
      showLegend: false,
      type: 'pie',
      textinfo: 'value',
      textposition: 'inside',
      automargin: true,
      insidetextfont: {
        family: 'Inter',
        size: 13,
        color: color('paneBackground'),
        weight: 900
      },
      outsidetextfont: {
        family: 'Inter',
        size: 11,
        color: color('thirdHeaderText')
      },
      hovertemplate: '%{label}: ' + '<br>%{value}<br>' + '%{percent}<br>' + '<extra></extra>',
      hoverlabel: { bgcolor: color('paneBackground'), font: { color: color('tertiaryButtonHoverText'), size: 11, family: 'Inter' } }
    }
  })

  const buttons = [
    {
      text: 'Connected',
      quantity: connectedCount,
      accentColor: color('connected'),
      apptentiveEventName: 'view_connected_devices',
      filter: 'connected',
      tooltip: 'Total number of devices that are connected (includes "archived" thermostats).'
    },
    {
      text: 'Disconnected',
      quantity: disconnectedCount,
      accentColor: color('disconnected'),
      apptentiveEventName: 'view_disconnected_devices',
      hasStripedAccentColor: true,
      stripSize: ['5px', '6px'],
      filter: 'disconnected',
      tooltip: 'Total number of devices that are disconnected (includes "archived" thermostats).'
    }
  ]
  const redirectToCustomer = (ev: Plotly.PlotMouseEvent) => {
    const filters = ev.points[0].data.labels[ev.points[0].pointNumber]?.toString().toLowerCase() + ',' + ev.points[0].data.title.text
    navigate(`/customers?filters=${filters}`)
  }

  return (
    <Card title={ConnectionStatusCard.cardTitle} isLoaded={isLoaded && isDeviceLoaded}>
      <S.Description>Connection status is number of connected and disconnected devices.</S.Description>
      <S.StatusContainer>
        <S.Image src={'/img/ConnectionStatus.png'} imageWidth={imageWidth} />
        <S.Container>
          <S.Buttons>
            {buttons.map(button => <StatusButton {...button} key={button.text} />)}
          </S.Buttons>
          {
            plotDatas?.length > 0 && (
              <S.PlotArea>
                {plotDatas.map((plotData, key) => {
                  /* eslint-disable react/no-array-index-key */
                  return (
                    <S.Plot
                      key={key}
                      data={[plotData]}
                      layout={{
                        height: chartWidth,
                        width: chartWidth,
                        showlegend: false,
                        margin: { t: 0, b: 0, l: 0, r: 0 }
                      }}
                      config={{
                        displayModeBar: false,
                        showLink: false,
                        responsive: true

                      }}
                      onClick={redirectToCustomer}
                    />
                  )
                /* eslint-enable react/no-array-index-key */
                })
              }
              </S.PlotArea>
            )
          }
        </S.Container>
      </S.StatusContainer>
    </Card>
  )
}

ConnectionStatusCard.cardTitle = 'Connection Status'
ConnectionStatusCard.cardKey = 'connection_status'

const S = {
  Description: styled.div`
      font-size: 10pt;
      font-family: inter,regular;
      color: ${color('cardDescription')};
      min-height: 60px;
      @media ${screenSize.small} {
        min-height: unset;
      }
    `,
  Image: styled.img.withConfig({
    shouldForwardProp: (prop) => !['imageWidth'].includes(prop)
  })<{ imageWidth: number }>`
      display: inline-block;
      height: ${({ imageWidth }) => imageWidth}px;
      width: ${({ imageWidth }) => imageWidth}px;
      opacity: 0.9;
    `,
  StatusContainer: styled.div`
      display: flex;
      margin-top: 10px;
    `,
  Container: styled.div`
      margin-left: 20px;
      width: 100%;
      display: flex;
      flex-direction:column;
      gap: 10px;
      @media ${screenSize.small} {
        margin-left: 15px;
      }
    `,
  PlotArea: styled.div`
      display: flex;
      flex-wrap: wrap;
      border: 1px solid ${color('border')};
      justify-content: flex-start;
      border-radius: 6px;
      padding: 10px;
    `,
  Plot: styled(Plot)`
    width: 33%;
    @media ${screenSize.small} {
      width: auto;
    }
  `,
  Buttons: styled.div`
      display: flex;
      flex-direction: column;
      gap: 10px;
      @media ${screenSize.mediumOnly} {
        flex: 0 0 30%;
      }
    `
}

export default ConnectionStatusCard
